/**
 * The office's part of a visit's status — `docs/visit-page.md` §17 (Board 13
 * B) and §26.7 (Board 17 F) — and the visit's own edit sheet (§7.3 About
 * *Edit*, Board 13 B *Edit plan*).
 *
 * `work_items.state` is `open · on_hold · cancelled · closed`; the rest of the
 * status is derived from the days (§17.1). Nothing deletes a visit: a visit
 * made from a quote is cancelled, kept and counted (§26.7), and there is no
 * DELETE route for one.
 */
import { z } from 'zod';
import { APPLY_TO } from './crew.js';
const reason = z.string().trim().min(1, 'Say why.').max(2000);
/**
 * `POST /visits/:id/hold {reason}` — ⋯ **Put on hold** asks why. Its days come
 * off the schedule and its holds at the yard are released (§17.3): *Held by
 * <who> <date>: <reason>.*
 */
export const holdVisitSchema = z.object({ reason });
/**
 * `POST /visits/:id/release` — **Take off hold**. §5.2 gives all three
 * `{reason}`; Board 13 B draws no question for this one, so it is optional.
 */
export const releaseVisitSchema = z.object({
    reason: z.string().trim().max(2000).default(''),
});
/**
 * `POST /visits/:id/cancel {reason, tellSalesperson}` — ⋯ **Cancel the visit**
 * (§17; for a visit from a quote, Board 17 F, §26.7): **Why** (required; the
 * button is disabled until it has text) and ☑ ***Tell Tom Reyes*** *— a change
 * order may be needed* (on; a task for the job's salesperson).
 *
 * PR 1 built this endpoint (`server/src/routes/quote-visits.ts`, its own
 * `cancelBody` with the same rules); this is the shared copy the page and that
 * route should both read.
 */
export const cancelVisitSchema = z.object({
    reason: z.string().trim().min(1, 'Say why.').max(2000),
    tellSalesperson: z.boolean().default(true),
});
/**
 * `PATCH /visits/:id` — the visit's edit sheet (About *Edit*; *Edit plan* while
 * unscheduled): the name, the crew, the yard, the planned hours. The plan
 * lines are never edited (§2.5). On a series visit the sheet asks *how far*
 * (Board 14, D20): this visit only, or this one and the ones after; visits
 * already closed never change.
 */
export const editVisitSchema = z.object({
    version: z.number().int().min(1),
    name: z.string().trim().min(1, 'Give it a name.').max(200).optional(),
    crewId: z.string().uuid().nullable().optional(),
    yardId: z.string().uuid().nullable().optional(),
    plannedHours: z.number().positive().max(10_000).optional(),
    applyTo: z.enum(APPLY_TO).default('this'),
}).refine((body) => body.name !== undefined || body.crewId !== undefined
    || body.yardId !== undefined || body.plannedHours !== undefined, { message: 'Nothing to change.' });
//# sourceMappingURL=states.js.map